import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import { toast } from "../../helpers/swal";
import DallasShopNav from "../../components/common/DallasShopNav";
import {
  mergeBlankOrderItems,
  parseBlankOrdersWorkbook,
} from "../../helpers/functions/parse-blank-orders-excel";

const emptyForm = {
  brand: "",
  style: "",
  product: "",
  color: "",
  size: "",
  jiffy_variant_id: "",
  gtin: "",
  units_per_case: "",
  quantity: "",
  notes: "",
};

const qtyColor = (qty) => {
  const n = Number(qty);
  if (n <= 0) return { background: "#fecaca", color: "#7f1d1d", fontWeight: 700 };
  if (n < 5) return { background: "#fee2e2", color: "#991b1b", fontWeight: 700 };
  if (n < 15) return { background: "#fef3c7", color: "#92400e" };
  return { background: "#dcfce7", color: "#166534" };
};

const DallasInventoryPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [summary, setSummary] = useState({
    sku_count: 0,
    total_units: 0,
    low_stock_count: 0,
    out_of_stock_count: 0,
    today_in: 0,
    today_out: 0,
    by_style: [],
  });
  const [styles, setStyles] = useState([]);
  const [search, setSearch] = useState("");
  const [styleFilter, setStyleFilter] = useState("All");
  const [lowOnly, setLowOnly] = useState(false);
  const [editQty, setEditQty] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [showAdd, setShowAdd] = useState(false);
  const [importPreview, setImportPreview] = useState(null);

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (styleFilter && styleFilter !== "All") params.set("style", styleFilter);
      if (lowOnly) params.set("low_only", "1");
      const response = await axios.get(
        `${settings.jiffyAPI}/get_dallas_inventory.php?${params.toString()}`,
        { headers: authHeader() }
      );
      if (!response.data.success) {
        toast(response.data.error || "Inventory load failed", "error");
        return;
      }
      setItems(response.data.items || []);
      setMovements(response.data.movements || []);
      setSummary(response.data.summary || {});
      setStyles(response.data.styles || []);
      const nextEdits = {};
      (response.data.items || []).forEach((item) => {
        nextEdits[item.id] = String(item.quantity);
      });
      setEditQty(nextEdits);
    } catch (err) {
      toast("Inventory load failed", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventory();
  }, [styleFilter, lowOnly]);

  const filteredCount = items.length;

  const styleTotals = useMemo(
    () => summary.by_style || [],
    [summary.by_style]
  );

  const saveQuantity = async (item, rawQty) => {
    const quantity = Number(rawQty);
    if (Number.isNaN(quantity) || quantity < 0) {
      toast("Quantity must be 0 or more", "warning");
      return;
    }
    if (quantity === Number(item.quantity)) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${settings.jiffyAPI}/update_dallas_inventory.php`,
        {
          action: "set",
          id: item.id,
          sku_key: item.sku_key,
          brand: item.brand,
          style: item.style,
          product: item.product,
          color: item.color,
          size: item.size,
          jiffy_variant_id: item.jiffy_variant_id,
          gtin: item.gtin,
          units_per_case: item.units_per_case,
          quantity,
          notes: "Manual edit from inventory page",
        },
        { headers: authHeader() }
      );
      if (!response.data.success) {
        toast(response.data.error || "Update failed", "error");
        return;
      }
      toast("Quantity updated", "success");
      fetchInventory();
    } catch (err) {
      toast("Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const adjustItem = async (item, delta) => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${settings.jiffyAPI}/update_dallas_inventory.php`,
        {
          action: "adjust",
          ...item,
          delta,
          notes: delta < 0 ? "Manual out" : "Manual add",
        },
        { headers: authHeader() }
      );
      if (!response.data.success) {
        toast(response.data.error || "Update failed", "error");
        return;
      }
      fetchInventory();
    } catch (err) {
      toast("Update failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const addItem = async (event) => {
    event.preventDefault();
    if (!form.style && !form.jiffy_variant_id) {
      toast("Style or Jiffy Variant ID is required", "warning");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${settings.jiffyAPI}/update_dallas_inventory.php`,
        {
          action: "create",
          ...form,
          qty: Number(form.quantity || 0),
          units_per_case: Number(form.units_per_case || 0),
          source_label: "Manual add",
        },
        { headers: authHeader() }
      );
      if (!response.data.success) {
        toast(response.data.error || "Add failed", "error");
        return;
      }
      toast("Item added", "success");
      setForm(emptyForm);
      setShowAdd(false);
      fetchInventory();
    } catch (err) {
      toast("Add failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const onExcelSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseBlankOrdersWorkbook(buffer);
      const merged = mergeBlankOrderItems(parsed);
      if (!merged.length) {
        toast("No shirt rows found in that Excel file", "warning");
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: merged,
        units: merged.reduce((sum, item) => sum + Number(item.qty || 0), 0),
      });
    } catch (err) {
      toast("Could not read Excel file", "error");
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.items?.length) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${settings.jiffyAPI}/import_dallas_inventory.php`,
        {
          source_label: importPreview.fileName,
          items: importPreview.items,
        },
        { headers: authHeader() }
      );
      if (!response.data.success) {
        toast(response.data.error || "Import failed", "error");
        return;
      }
      toast(
        `Added ${response.data.imported_units} shirts across ${response.data.touched_skus} SKUs`,
        "success"
      );
      setImportPreview(null);
      fetchInventory();
    } catch (err) {
      toast("Import failed", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid mt-4 px-2">
      <DallasShopNav active="inventory" />

      <div className="row g-3 mb-3">
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100 bg-white">
            <div className="text-muted small">Total shirts</div>
            <div className="fs-4 fw-bold">{summary.total_units || 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100 bg-white">
            <div className="text-muted small">SKUs</div>
            <div className="fs-4 fw-bold">{summary.sku_count || 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100 bg-white">
            <div className="text-muted small">Today in</div>
            <div className="fs-4 fw-bold text-success">{summary.today_in || 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100 bg-white">
            <div className="text-muted small">Today out</div>
            <div className="fs-4 fw-bold text-danger">{summary.today_out || 0}</div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100" style={{ background: "#fee2e2" }}>
            <div className="text-muted small">Below 5</div>
            <div className="fs-4 fw-bold text-danger">
              {summary.low_stock_count || 0}
            </div>
          </div>
        </div>
        <div className="col-6 col-md-2">
          <div className="border rounded p-3 h-100" style={{ background: "#fecaca" }}>
            <div className="text-muted small">Out of stock</div>
            <div className="fs-4 fw-bold" style={{ color: "#7f1d1d" }}>
              {summary.out_of_stock_count || 0}
            </div>
          </div>
        </div>
      </div>

      {styleTotals.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {styleTotals.map((row) => (
            <span key={row.style} className="badge text-bg-light border">
              {row.style}: {row.units}
            </span>
          ))}
        </div>
      )}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <input
          type="text"
          className="form-control"
          style={{ maxWidth: 260 }}
          placeholder="Search style, color, size, GTIN..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") fetchInventory();
          }}
        />
        <select
          className="form-select"
          style={{ maxWidth: 180 }}
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
        >
          <option value="All">All styles</option>
          {styles.map((style) => (
            <option key={style} value={style}>
              {style}
            </option>
          ))}
        </select>
        <label className="form-check d-flex align-items-center gap-2 mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => setLowOnly(e.target.checked)}
          />
          Below 5 only
        </label>
        <button className="btn btn-outline-secondary" onClick={fetchInventory}>
          Refresh
        </button>
        <label className="btn btn-outline-primary mb-0">
          Import Excel
          <input
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={onExcelSelected}
          />
        </label>
        <button
          className="btn btn-primary"
          onClick={() => setShowAdd((open) => !open)}
        >
          {showAdd ? "Close add form" : "Add shirts"}
        </button>
        <span className="ms-auto text-muted">Showing {filteredCount} SKUs</span>
      </div>

      {importPreview && (
        <div className="alert alert-info d-flex justify-content-between align-items-center">
          <div>
            <strong>{importPreview.fileName}</strong> — {importPreview.items.length}{" "}
            SKUs, {importPreview.units} shirts will be added on top of current stock.
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setImportPreview(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-sm btn-success"
              disabled={saving}
              onClick={confirmImport}
            >
              {saving ? "Importing..." : "Add to inventory"}
            </button>
          </div>
        </div>
      )}

      {showAdd && (
        <form className="border rounded p-3 mb-3 bg-white" onSubmit={addItem}>
          <div className="row g-2">
            {[
              ["brand", "Brand"],
              ["style", "Style"],
              ["product", "Product"],
              ["color", "Color"],
              ["size", "Size"],
              ["jiffy_variant_id", "Jiffy Variant ID"],
              ["gtin", "GTIN"],
              ["units_per_case", "Units / case"],
              ["quantity", "Qty to add"],
              ["notes", "Notes"],
            ].map(([key, label]) => (
              <div className="col-6 col-md-3" key={key}>
                <label className="form-label small mb-1">{label}</label>
                <input
                  className="form-control form-control-sm"
                  value={form[key]}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>
          <button className="btn btn-success mt-3" disabled={saving} type="submit">
            {saving ? "Saving..." : "Add to inventory"}
          </button>
        </form>
      )}

      {loading ? (
        <p>Loading inventory...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Style</th>
                <th>Product</th>
                <th>Color</th>
                <th>Size</th>
                <th>Variant ID</th>
                <th>On hand</th>
                <th style={{ minWidth: 160 }}>Edit</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} style={qtyColor(item.quantity)}>
                  <td>{item.brand}</td>
                  <td>{item.style}</td>
                  <td>{item.product}</td>
                  <td>{item.color}</td>
                  <td>{item.size}</td>
                  <td>{item.jiffy_variant_id}</td>
                  <td className="fw-bold">{item.quantity}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={saving}
                        onClick={() => adjustItem(item, -1)}
                      >
                        -1
                      </button>
                      <input
                        className="form-control form-control-sm"
                        style={{ width: 70 }}
                        value={editQty[item.id] ?? ""}
                        onChange={(e) =>
                          setEditQty((prev) => ({
                            ...prev,
                            [item.id]: e.target.value,
                          }))
                        }
                        onBlur={() => saveQuantity(item, editQty[item.id])}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.target.blur();
                          }
                        }}
                      />
                      <button
                        className="btn btn-sm btn-outline-success"
                        disabled={saving}
                        onClick={() => adjustItem(item, 1)}
                      >
                        +1
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h5 className="mt-4">Recent movement</h5>
      <div className="table-responsive">
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th>When</th>
              <th>Style</th>
              <th>Color / Size</th>
              <th>Dir</th>
              <th>Qty</th>
              <th>Before</th>
              <th>After</th>
              <th>Reason</th>
              <th>PO / source</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((row) => (
              <tr key={row.id}>
                <td>{row.created_at}</td>
                <td>{row.style || row.sku_key}</td>
                <td>
                  {row.color} {row.size}
                </td>
                <td>{row.direction}</td>
                <td>{row.quantity}</td>
                <td>{row.quantity_before}</td>
                <td>{row.quantity_after}</td>
                <td>{row.reason}</td>
                <td>
                  {row.po_number} {row.source_label}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DallasInventoryPage;
